//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//
import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from '@carbon/icons-react';
import { Button } from '../ui/button';
import { TeamspaceIdentifier } from '../Sidebar/SidebarTypes';
import { useNavigate } from 'react-router-dom';
import { useTeamspaceContext } from '../../TeamspaceContext';
import storageService from '../../utils/storageService';
import { failedLoop } from '../../LoopContext';

interface CreationFailedBannerProps {
  bannerOpen: boolean;
  setBannerOpen: (open: boolean) => void;
  failedTeamspaces: TeamspaceIdentifier[];
  failedLoops: failedLoop[];
}

const CreationFailedBanner = ({
  bannerOpen,
  setBannerOpen,
  failedTeamspaces,
  failedLoops,
}: Readonly<CreationFailedBannerProps>) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    _setTeamspaceId,
    _setTeamspaceName,
    _setTeamspaceSlug,
    _setCreateNewTeamSpace,
    _setIncompleteTeamspaceTask,
  } = useTeamspaceContext();
  const [currentStep, setCurrentStep] = useState(0);

  const failedItems = useMemo(() => {
    return [...(failedTeamspaces || []), ...(failedLoops || [])];
  }, [failedTeamspaces, failedLoops]);

  const safeStep =
    failedItems.length > 0 ? Math.min(currentStep, failedItems.length - 1) : 0;

  const handleBannerClose = () => {
    storageService.setSessionItem(storageService.KEYS.BANNER_DISMISSED, 'true');
    setBannerOpen(false);
  };

  const handleNext = () => {
    setCurrentStep((prevStep) =>
      Math.min(prevStep + 1, failedItems.length - 1),
    );
  };

  const handleBack = () => {
    setCurrentStep((prevStep) => Math.max(prevStep - 1, 0));
  };

  const handleRetry = () => {
    // Clear dismissed state so the banner can reappear if the retry fails again
    storageService.removeSessionItem(storageService.KEYS.BANNER_DISMISSED);
    // Set teamspace Id to retry and navigate to teamspace page to retry creation
    const item = failedItems[safeStep];
    if (!item || item.mode === 'loop') return;
    const tsItem = item as TeamspaceIdentifier;
    _setCreateNewTeamSpace(false);
    _setTeamspaceId(tsItem.id);
    _setTeamspaceName(tsItem.name);
    _setTeamspaceSlug(tsItem.slug);
    // If teamspace has an incomplete task, then set it in context on retry
    if (tsItem.incompleteTask) {
      _setIncompleteTeamspaceTask(tsItem.incompleteTask ?? '');
    }
    storageService.setCurrentTeamspace(tsItem.id, tsItem.name);
    navigate('/teamspace');
  };

  useEffect(() => {
    if (failedItems.length === 0) {
      setBannerOpen(false);
    }
  }, [failedItems.length, setBannerOpen]);

  if (!bannerOpen || !failedItems.length) return null;

  const currentItem = failedItems[safeStep];
  if (!currentItem) return null;
  const isLoop = currentItem.mode === 'loop';

  const bannerClass = isLoop
    ? 'border-yellow-500/60 bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-100'
    : 'border-red-500/60 bg-red-100 text-red-900 dark:bg-red-900 dark:text-red-100';

  return (
    <div
      data-testid={'id-creation-failed-banner-container'}
      className="mt-2 flex"
    >
      <div
        data-testid={'id-creation-failed-banner-alert'}
        className={`flex min-w-[280px] max-w-[520px] items-center gap-3 rounded border px-3 py-1.5 ${bannerClass}`}
      >
        {/* Message content */}
        <div
          data-testid={'id-creation-failed-banner-warning-container'}
          className="min-w-0 flex-1"
        >
          <div className="mb-0.5 flex items-center justify-between">
            <p className="text-sm font-semibold">
              {t(
                isLoop
                  ? 'creation-failed-loop-title'
                  : 'creation-failed-teamspace-title',
              )}
            </p>
            <span className="ml-2 shrink-0 text-[11px] font-semibold">
              {safeStep + 1}/{failedItems.length}
            </span>
          </div>
          <p className="text-xs leading-5">
            {isLoop
              ? t('creation-failed-loop-description')
              : t('creation-failed-teamspace-description')}
          </p>
        </div>

        {/* Navigation + Dismiss */}
        <div
          data-testid={'id-creation-failed-banner-back-button-container'}
          className="flex shrink-0 items-center gap-1"
        >
          <Button
            data-testid={'id-creation-failed-banner-back-button'}
            variant="ghost"
            size="icon"
            className="size-6 text-current hover:bg-white/30 disabled:opacity-50"
            onClick={handleBack}
            disabled={safeStep === 0}
            aria-label="Previous"
          >
            <ChevronLeft size={16} />
          </Button>
          <div
            data-testid={'id-creation-failed-banner-next-button-container'}
            className="flex items-center"
          >
            <Button
              data-testid={'id-creation-failed-banner-next-button'}
              variant="ghost"
              size="icon"
              className="size-6 text-current hover:bg-white/30 disabled:opacity-50"
              onClick={handleNext}
              disabled={safeStep === failedItems.length - 1}
              aria-label="Next"
            >
              <ChevronRight size={16} />
            </Button>
          </div>
          {/** Only display retry button for teamspaces,
           *   do not display in Loop failed banner as the user can
           *   navigate there from the incomplete loops tab
           **/}
          {!isLoop && (
            <Button
              data-testid={'id-creation-banner-retry-button'}
              variant="ghost"
              size="sm"
              className="h-auto rounded border border-current px-1.5 py-0.5 text-xs text-current hover:bg-white/30"
              onClick={handleRetry}
            >
              {t('retry-now')}
            </Button>
          )}
          <Button
            data-testid={'id-creation-failed-banner-dismiss-button'}
            variant="ghost"
            size="sm"
            className="h-auto px-1.5 py-0.5 text-sm font-medium text-current hover:bg-white/30"
            onClick={handleBannerClose}
          >
            {t('Dismiss')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreationFailedBanner;
